import React from "react";
import { PropTypes } from "prop-types";
// Material imports
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { useMessagePass } from "../../../../expert";
import { useExpertProfileContext, useExpertTaskListContext, useExpertMessageContext } from "@limitless/expert";
import { withi18n } from "../../../../customer/components";
import { PortalButton, isQualityReviewState } from "../../..";
import PanelHeader from "../panel-header/PanelHeader";
import { useKnowledgeLibraryContext } from "../../../hooks/knowledge/KnowledgeLibraryContext";
import { aiStar } from "@limitless/images";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import { useWebSockets } from "@limitless/common/hooks/web-sockets/WebSocketsContext";
import { useExpertAIReviewContext } from "../../../../expert/hooks/expert-ai-review/ExpertAIReviewManagerContext";
import AIIconAnimation from "@limitless/modern/components/ai-icon-animation/AIIconAnimation";
const ConversationHeader = ({
  classes,
  t,
  message,
  clearMessage,
  translationLanguage
}) => {
  var _message$aiRespond3, _passiveRespondEventD, _message$aiRespond6;
  const [clearMessageTimeout, setClearTimeout] = React.useState();
  const [isDisable, setIsDisable] = React.useState(false);
  const log = pilog.getLogger("ConversationHeader");
  const {
    passMessage
  } = useMessagePass();
  const {
    tasklistFilters
  } = useExpertTaskListContext();
  const {
    clearMessageAndUnclaim
  } = useExpertMessageContext();
  const {
    fetchAndSetAIReviewData,
    handleAIDraftSelected,
    isAIDraftSelected
  } = useExpertAIReviewContext();
  const [showAnimatedIcon, setShowAnimatedIcon] = React.useState(true);
  const {
    conversationState
  } = message;
  const {
    profile
  } = useExpertProfileContext();
  const isReviewDetailedInbox = isQualityReviewState(message.conversationState.state);
  const {
    setLibraryToLoad
  } = useKnowledgeLibraryContext();
  const [isAiRespondCreatedAt, setIsAiRespondCreatedAt] = React.useState(false);
  const {
    passiveRespondEventData,
    passiveRespondEvent,
    setPassiveRespondEvent,
    setPassiveRespondEventData
  } = useWebSockets();

  // This checks if the AI draft is ready to be used for the current message.
  // The "Use AI Draft" button should only be enabled when:
  // - The AI response flag is true (AI event has finished processing), and
  // - The flag belongs to this specific message (matching messageId).
  const useAIDraftEnabled = (passiveRespondEvent === null || passiveRespondEvent === void 0 ? void 0 : passiveRespondEvent.buttonEnabled) && (passiveRespondEvent === null || passiveRespondEvent === void 0 ? void 0 : passiveRespondEvent.messageId) === message.messageId;
  React.useEffect(() => {
    //clear the timeout incase this component unmounts before it executes
    return () => {
      if (clearMessageTimeout) {
        clearTimeout(clearMessageTimeout);
      }
    };
  }, [clearMessageTimeout]);

  //stops animation of ai after first event arrived
  React.useEffect(() => {
    if (!passiveRespondEvent.animation && showAnimatedIcon) {
      setShowAnimatedIcon(false);
    }
    if (passiveRespondEvent.animation && !showAnimatedIcon) {
      setShowAnimatedIcon(true);
    }
    if (passiveRespondEvent.flag && isAiRespondCreatedAt) {
      setIsAiRespondCreatedAt(false);
    }
  }, [passiveRespondEvent.animation, showAnimatedIcon, isAiRespondCreatedAt, passiveRespondEvent.failed, passiveRespondEvent.flag]);
  React.useEffect(() => {
    var _message$aiRespond, _message$aiRespond2;
    if (message !== null && message !== void 0 && (_message$aiRespond = message.aiRespond) !== null && _message$aiRespond !== void 0 && _message$aiRespond.createdAt) {
      setPassiveRespondEvent(prev => ({
        ...prev,
        flag: false,
        failed: false,
        animation: false,
        buttonEnabled: true,
        messageId: message.messageId
      }));
      setShowAnimatedIcon(false);
      setIsAiRespondCreatedAt(true);
    } else if (message !== null && message !== void 0 && (_message$aiRespond2 = message.aiRespond) !== null && _message$aiRespond2 !== void 0 && _message$aiRespond2.isAIRespondFailed) {
      setPassiveRespondEvent(prev => ({
        ...prev,
        flag: false,
        failed: false,
        buttonEnabled: false,
        animation: false,
        messageId: message.messageId
      }));
      setShowAnimatedIcon(false);
    } // This resets the AI response event state for the current message
    // when the AI draft is not available (flag is false or belongs to a different message).
    // It helps prevent showing outdated AI info from a previously opened message.
    else if (!useAIDraftEnabled) {
      setPassiveRespondEvent(prev => ({
        ...prev,
        flag: false,
        failed: false,
        buttonEnabled: false,
        animation: true,
        messageId: message.messageId
      }));
      setShowAnimatedIcon(true);
      setIsAiRespondCreatedAt(false);
      setPassiveRespondEventData({
        payload: null
      });
    }
  }, [message.messageId]);
  const handleUseAIDraftClick = () => {
    const messageId = message.messageId;
    fetchAndSetAIReviewData(messageId, message.aiRespond, isAiRespondCreatedAt);
    handleAIDraftSelected(true);
    setPassiveRespondEvent(prev => ({
      ...prev,
      buttonEnabled: false
    }));
  };
  let conversationTitle = isReviewDetailedInbox && t("Review");
  if (conversationState.showCustomerAuthor && message.author) {
    conversationTitle = t(isReviewDetailedInbox ? "Review the conversation with {0}" : "{0}", message.author.charAt(0).toUpperCase() + message.author.slice(1));
  }
  const handlePassMessage = () => {
    passMessage(true, message);
    clearMessageAndUnclaim();
  };
  const isExpertFirst = React.useMemo(() => {
    var _message$dialogues$fi, _message$dialogues;
    return (_message$dialogues$fi = message === null || message === void 0 ? void 0 : (_message$dialogues = message.dialogues) === null || _message$dialogues === void 0 ? void 0 : _message$dialogues.find(dialogue => {
      return (dialogue === null || dialogue === void 0 ? void 0 : dialogue.origin) === "expert" && (dialogue === null || dialogue === void 0 ? void 0 : dialogue.userId) === (profile === null || profile === void 0 ? void 0 : profile.userId);
    })) !== null && _message$dialogues$fi !== void 0 ? _message$dialogues$fi : 0;
  });
  React.useEffect(() => {
    if (message !== null && message !== void 0 && message.knowledgeBaseIsActive) {
      setLibraryToLoad({
        groupName: message.groupName,
        tenantName: message.tenantName
      });
    }
  }, []);
  log.debug("render");
  let aiDraftLabel = "";
  if (message !== null && message !== void 0 && (_message$aiRespond3 = message.aiRespond) !== null && _message$aiRespond3 !== void 0 && _message$aiRespond3.createdAt && !passiveRespondEvent.failed && !passiveRespondEvent.flag) {
    var _message$aiRespond$re, _message$aiRespond4, _message$aiRespond4$r, _message$aiRespond$re2, _message$aiRespond5, _message$aiRespond5$r;
    aiDraftLabel = `${t("Next Dialogue")}: ${(_message$aiRespond$re = message === null || message === void 0 ? void 0 : (_message$aiRespond4 = message.aiRespond) === null || _message$aiRespond4 === void 0 ? void 0 : (_message$aiRespond4$r = _message$aiRespond4.respondNextDialogue) === null || _message$aiRespond4$r === void 0 ? void 0 : _message$aiRespond4$r.nextDialogue) !== null && _message$aiRespond$re !== void 0 ? _message$aiRespond$re : ""}\n${t("Reasoning")}: ${(_message$aiRespond$re2 = message === null || message === void 0 ? void 0 : (_message$aiRespond5 = message.aiRespond) === null || _message$aiRespond5 === void 0 ? void 0 : (_message$aiRespond5$r = _message$aiRespond5.respondNextDialogue) === null || _message$aiRespond5$r === void 0 ? void 0 : _message$aiRespond5$r.reasoning) !== null && _message$aiRespond$re2 !== void 0 ? _message$aiRespond$re2 : ""}`;
  } else if (passiveRespondEventData !== null && passiveRespondEventData !== void 0 && (_passiveRespondEventD = passiveRespondEventData.payload) !== null && _passiveRespondEventD !== void 0 && _passiveRespondEventD.respondNextDialogue) {
    var _data$nextDialogue, _data$reasoning;
    const data = passiveRespondEventData.payload.respondNextDialogue;
    aiDraftLabel = `${t("Next Dialogue")}: ${(_data$nextDialogue = data.nextDialogue) !== null && _data$nextDialogue !== void 0 ? _data$nextDialogue : ""}\n${t("Reasoning")}: ${(_data$reasoning = data.reasoning) !== null && _data$reasoning !== void 0 ? _data$reasoning : ""}`;
  } else if (passiveRespondEvent.failed || message !== null && message !== void 0 && (_message$aiRespond6 = message.aiRespond) !== null && _message$aiRespond6 !== void 0 && _message$aiRespond6.isAIRespondFailed) {
    aiDraftLabel = t("AI draft unavailable. Try again later.");
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes.conversationHeader
  }, /*#__PURE__*/React.createElement(PanelHeader, {
    borderBottom: false
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, conversationTitle), !isReviewDetailedInbox && /*#__PURE__*/React.createElement(Typography, {
    className: classes.displayName,
    variant: !conversationTitle ? "h6" : "caption"
  }, message.displayName)), /*#__PURE__*/React.createElement("div", {
    className: classes.passWrapper
  }, message.conversationState.showTransaltedLanguageText && /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    color: theme => theme.palette.text.primary
  }, t("Customer’s messages are translated from {0}.", translationLanguage)), message.isAIAssistEnabled && !message.qualityReviewGroup && !message.practiceGroup && /*#__PURE__*/React.createElement("div", null, showAnimatedIcon ? /*#__PURE__*/React.createElement(AIIconAnimation, null) : /*#__PURE__*/React.createElement(IconButton, {
    icon: /*#__PURE__*/React.createElement("img", {
      src: aiStar,
      alt: "AI Star",
      className: classes.aiIconImage
    }),
    className: classes.aiIconOffset,
    label: aiDraftLabel
  }), /*#__PURE__*/React.createElement(Button, {
    id: "useAiDraftButton",
    variant: "contained",
    onClick: handleUseAIDraftClick,
    disabled: !useAIDraftEnabled
  }, t("Use ai draft"))), !tasklistFilters.passedTasks.pass.value && isExpertFirst === 0 && /*#__PURE__*/React.createElement(PortalButton, {
    id: "passBtn",
    variant: "outlined",
    onClick: () => {
      setIsDisable(true);
      handlePassMessage();
    },
    classes: classes,
    disabled: isDisable,
    style: {
      marginLeft: 10
    }
  }, "Pass"))));
};
const styles = theme => ({
  container: {
    display: "flex",
    flexDirection: "row",
    color: theme.palette.text.primary
  },
  displayName: {
    marginLeft: 12,
    marginBottom: 2,
    alignItems: "end",
    display: "flex"
  },
  conversationHeader: {
    marginLeft: 16,
    marginRight: 16
  },
  outlinePortalButton: {
    borderColor: theme.palette.primary.main,
    "&:hover": {
      cursor: "pointer",
      borderColor: theme.palette.primary.light
    }
  },
  passWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-end"
  },
  aiIconImage: {
    width: 30,
    height: 30
  },
  aiIconOffset: {
    position: "absolute",
    zIndex: 10,
    marginTop: -9,
    marginLeft: 10
  }
});
export default withStyles(styles)(withi18n(ConversationHeader));
ConversationHeader.propTypes = {
  message: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  translationLanguage: PropTypes.string,
  clearMessage: PropTypes.func.isRequired,
  onFetchAIDraft: PropTypes.func.isRequired,
  aiReviewData: PropTypes.object,
  onAIDraftSelected: PropTypes.func
};